import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import type { StockSector } from "@richrich/contracts";
import { paletteFor, hexToRgba, type VizPalette } from "../lib/palette";
import { layoutTreemap, type TreemapTile } from "../lib/treemap";
import { useResolvedTheme, type Theme } from "../lib/theme";
import { cn } from "../lib/cn";

/** One treemap cell — a single stock, sized by `weight`, tinted by `changePercent`. */
export interface StockTreemapTile {
  symbol: string;
  name: string;
  /** SPDR sector the stock rolls up to (its grouping region). */
  sector: StockSector;
  /** Cap-proportional weight — drives tile AREA (not color). */
  weight: number;
  /** Last trade; `null` when the bulk quote missed it → gray tile. */
  price: number | null;
  /** Signed % vs. the prev-close baseline; `null` when no baseline yet → neutral tile. */
  changePercent: number | null;
}

export interface StockTreemapProps {
  stocks: StockTreemapTile[];
  /**
   * When set, ONLY this sector's stocks fill the whole area (the drill-down
   * view) — no sector regions or labels. When null/undefined, all sectors are
   * grouped: sectors squarified first, each sector's stocks squarified inside.
   */
  selectedSector?: StockSector | null;
  /** Grouped view: clicking a sector region reports its ETF symbol to drill in. */
  onSelectSector?: (sector: StockSector) => void;
  /** ETF symbol → localized region label; defaults to the symbol itself. */
  sectorLabel?: (sector: StockSector) => string;
  /** Format a tile's price for the hover title; defaults to a grouped number. */
  formatPrice?: (price: number) => string;
  theme?: Theme;
  /** Overall canvas height in px (width is measured responsively). */
  height?: number;
  /** role="img" summary; caller passes a localized sentence. */
  ariaLabel?: string;
  /** Empty-state copy when there are no stocks. */
  emptyLabel?: ReactNode;
  className?: string;
}

/** |change| that saturates the tile tint (mirrors {@link SectorHeatmap}). */
const CLAMP_PCT = 3;
/** Max tile-fill opacity at full saturation — kept moderate so text stays legible. */
const MAX_ALPHA = 0.62;
/** Min region box (px) to bother printing its sector label. */
const LABEL_MIN_W = 60;
const LABEL_MIN_H = 26;
/** Content tiers by tile box (px). */
const BIG_W = 52;
const BIG_H = 34;
const MED_W = 30;
const MED_H = 17;

/** Signed percent text — the printed sign is the non-color status channel. */
function signedPct(pct: number): string {
  const v = pct.toFixed(2);
  return pct > 0 ? `+${v}%` : `${v}%`;
}

/**
 * Tile fill for a change value: tint by |change|, or a hatched neutral for null.
 * Exported so a legend can render swatches with the exact ramp the tiles use.
 */
export function treemapTileStyle(
  changePercent: number | null,
  palette: VizPalette,
): { backgroundColor: string; backgroundImage?: string } {
  if (changePercent === null) {
    // Hatched muted fill — visually distinct from a real 0.00% (never faked).
    const line = hexToRgba(palette.muted, 0.22);
    return {
      backgroundColor: hexToRgba(palette.muted, 0.08),
      backgroundImage: `repeating-linear-gradient(45deg, ${line} 0, ${line} 1px, transparent 1px, transparent 5px)`,
    };
  }
  const intensity = Math.min(Math.abs(changePercent), CLAMP_PCT) / CLAMP_PCT;
  const base = changePercent >= 0 ? palette.good : palette.critical;
  return { backgroundColor: hexToRgba(base, intensity * MAX_ALPHA) };
}

/** Observe an element's content-box width (0 until first measure). */
function useMeasuredWidth<T extends HTMLElement>(): [React.RefObject<T | null>, number] {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width ?? 0;
      if (w > 0) setWidth(w);
    });
    ro.observe(el);
    setWidth(el.clientWidth);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
}

interface PlacedStock {
  tile: StockTreemapTile;
  x: number;
  y: number;
  w: number;
  h: number;
}

interface PlacedRegion {
  sector: StockSector;
  x: number;
  y: number;
  w: number;
  h: number;
  stocks: PlacedStock[];
}

/** Layout for the drill-down view: one sector's stocks fill the whole box. */
function layoutFlat(stocks: StockTreemapTile[], width: number, height: number): PlacedStock[] {
  const bySymbol = new Map(stocks.map((s) => [s.symbol, s]));
  return layoutTreemap(
    stocks.map((s) => ({ key: s.symbol, value: s.weight })),
    width,
    height,
  )
    .map((t) => placedFrom(t, bySymbol, 0, 0))
    .filter((p): p is PlacedStock => p !== null);
}

/** Layout for the full view: sectors squarified, then stocks within each region. */
function layoutGrouped(stocks: StockTreemapTile[], width: number, height: number): PlacedRegion[] {
  const groups = new Map<StockSector, StockTreemapTile[]>();
  for (const s of stocks) {
    const list = groups.get(s.sector);
    if (list) list.push(s);
    else groups.set(s.sector, [s]);
  }

  const sectorItems = [...groups].map(([sector, list]) => ({
    key: sector,
    value: list.reduce((sum, s) => sum + s.weight, 0),
  }));
  const sectorRects = layoutTreemap(sectorItems, width, height);

  const regions: PlacedRegion[] = [];
  for (const rect of sectorRects) {
    const sector = rect.key as StockSector;
    const list = groups.get(sector) ?? [];
    const bySymbol = new Map(list.map((s) => [s.symbol, s]));
    const stockRects = layoutTreemap(
      list.map((s) => ({ key: s.symbol, value: s.weight })),
      rect.w,
      rect.h,
    );
    const placed = stockRects
      .map((t) => placedFrom(t, bySymbol, rect.x, rect.y))
      .filter((p): p is PlacedStock => p !== null);
    regions.push({ sector, x: rect.x, y: rect.y, w: rect.w, h: rect.h, stocks: placed });
  }
  return regions;
}

function placedFrom(
  t: TreemapTile,
  bySymbol: Map<string, StockTreemapTile>,
  ox: number,
  oy: number,
): PlacedStock | null {
  const tile = bySymbol.get(t.key);
  if (!tile) return null;
  return { tile, x: t.x + ox, y: t.y + oy, w: t.w, h: t.h };
}

/** A single positioned stock cell. */
function StockCell({
  placed,
  palette,
  formatPrice,
}: {
  placed: PlacedStock;
  palette: VizPalette;
  formatPrice: (price: number) => string;
}): ReactNode {
  const { tile, x, y, w, h } = placed;
  const big = w >= BIG_W && h >= BIG_H;
  const medium = !big && w >= MED_W && h >= MED_H;
  const priceText = tile.price === null ? "—" : formatPrice(tile.price);
  const pctText = tile.changePercent === null ? "—" : signedPct(tile.changePercent);
  const title = `${tile.name} · ${priceText} · ${pctText}`;
  const style = treemapTileStyle(tile.changePercent, palette);
  // Type scales with the tile (finviz-style): mega-cap tiles read from across the
  // room, small caps stay legible-minimum. √area tracks perceived tile size.
  const scale = Math.sqrt(w * h);
  const symbolSize = Math.round(Math.min(24, Math.max(9, scale / 7)));
  const pctSize = Math.round(Math.min(17, Math.max(9, scale / 10)));

  return (
    <div
      className="absolute flex flex-col items-center justify-center overflow-hidden px-1 leading-none"
      style={{
        left: x,
        top: y,
        width: w,
        height: h,
        ...style,
        boxShadow: `inset 0 0 0 0.5px ${palette.surface}`,
      }}
      title={title}
    >
      {(big || medium) && (
        <span
          className="w-full truncate text-center font-mono font-semibold tracking-tight text-ink"
          style={{ fontSize: symbolSize }}
        >
          {tile.symbol}
        </span>
      )}
      {big && (
        <span
          className="mt-0.5 w-full truncate text-center font-medium tabular-nums text-ink"
          style={{ fontSize: pctSize }}
        >
          {pctText}
        </span>
      )}
    </div>
  );
}

/**
 * Per-stock treemap of the top-200 US names, grouped by SPDR sector. Tile AREA is
 * cap weight; tile COLOR interpolates the card surface toward good/critical by
 * |changePercent| (clamped ±3%, same ramp as {@link SectorHeatmap}); a null change
 * (no baseline / missing quote) is a hatched neutral tile, never a fake 0%. In the
 * grouped view each sector region is a button that drills into that sector; pass
 * `selectedSector` to render only that sector filling the whole canvas.
 */
export function StockTreemap({
  stocks,
  selectedSector,
  onSelectSector,
  sectorLabel,
  formatPrice,
  theme,
  height = 520,
  ariaLabel,
  emptyLabel = "No stock data",
  className,
}: StockTreemapProps): ReactNode {
  const resolved = useResolvedTheme(theme);
  const palette = paletteFor(resolved);
  const [ref, width] = useMeasuredWidth<HTMLDivElement>();
  const priceFmt = formatPrice ?? ((p: number) => p.toLocaleString());
  const labelFor = sectorLabel ?? ((s: StockSector) => s);

  const visible = useMemo(
    () => (selectedSector ? stocks.filter((s) => s.sector === selectedSector) : stocks),
    [stocks, selectedSector],
  );

  const flat = useMemo(
    () => (selectedSector && width > 0 ? layoutFlat(visible, width, height) : []),
    [selectedSector, visible, width, height],
  );
  const regions = useMemo(
    () => (!selectedSector && width > 0 ? layoutGrouped(visible, width, height) : []),
    [selectedSector, visible, width, height],
  );

  if (visible.length === 0) {
    return (
      <div
        className={cn(
          "flex items-center justify-center rounded-lg border border-dashed border-hairline text-sm text-muted",
          className,
        )}
        style={{ height }}
      >
        {emptyLabel}
      </div>
    );
  }

  const label =
    ariaLabel ??
    (selectedSector
      ? `${visible.length} stocks in ${labelFor(selectedSector)}`
      : `${visible.length} stocks across ${regions.length} sectors`);

  // Grouped view holds real sector buttons, so it's a labelled group (role="img"
  // would hide those buttons from assistive tech); the drill-down view is a
  // static picture, so it stays an image.
  const interactive = !selectedSector && !!onSelectSector;

  return (
    <div
      ref={ref}
      role={interactive ? "group" : "img"}
      aria-label={label}
      className={cn("relative w-full overflow-hidden rounded-lg", className)}
      style={{ height, backgroundColor: palette.surface }}
    >
      {selectedSector
        ? flat.map((p) => (
            <StockCell key={p.tile.symbol} placed={p} palette={palette} formatPrice={priceFmt} />
          ))
        : regions.map((region) => {
            const showLabel = region.w >= LABEL_MIN_W && region.h >= LABEL_MIN_H;
            const common = {
              className: "absolute overflow-hidden rounded-[3px] border border-hairline text-left",
              style: { left: region.x, top: region.y, width: region.w, height: region.h },
            };
            const inner = (
              <>
                {region.stocks.map((p) => (
                  <StockCell
                    key={p.tile.symbol}
                    placed={{ ...p, x: p.x - region.x, y: p.y - region.y }}
                    palette={palette}
                    formatPrice={priceFmt}
                  />
                ))}
                {showLabel && (
                  <span
                    className="pointer-events-none absolute left-1 top-1 z-10 rounded-sm px-1 py-px font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-ink-secondary"
                    style={{ backgroundColor: hexToRgba(palette.surface, 0.82) }}
                  >
                    {labelFor(region.sector)}
                  </span>
                )}
              </>
            );
            return onSelectSector ? (
              <button
                key={region.sector}
                type="button"
                onClick={() => onSelectSector(region.sector)}
                title={labelFor(region.sector)}
                // Subtree text (20 tickers) would otherwise become the accessible name.
                aria-label={labelFor(region.sector)}
                {...common}
                className={cn(
                  common.className,
                  "transition-shadow hover:shadow-[inset_0_0_0_2px_var(--color-brand)] focus-visible:shadow-[inset_0_0_0_2px_var(--color-brand)] focus-visible:outline-none",
                )}
              >
                {inner}
              </button>
            ) : (
              <div key={region.sector} {...common}>
                {inner}
              </div>
            );
          })}
    </div>
  );
}
